import { csrfHeaders } from "../auth/session-api.js";

/* Directory readiness — the inbound people/group/hierarchy sync status the
   Admin Console shows (PB-50 / AC-08), read from the PPL-07 status feed.

   Deliberately a SEPARATE client from tenant-config-api, because the two
   describe opposite directions and must never be merged into one view:
   `TenantSyncState` there counts the OUTBOUND user projection to Keycloak,
   while everything here is what the directory pushed or we pulled INBOUND. */

/**
 * Auth may widen these vocabularies ahead of the shell, and the guard below
 * deliberately accepts any string for them so a new value degrades through the
 * UI's fallback lookups instead of failing the whole read.
 *
 * The literals are what THIS build knows: they drive autocomplete and document
 * the contract. Narrowing the type to only them would make
 * `isDirectoryStatus`'s `value is DirectoryStatus` assert something it never
 * checks — and an exhaustive `switch` downstream would then compile without a
 * default and still meet an unknown value at runtime. `Widened` keeps the
 * documentation and the honesty at the same time.
 */
type Widened<T extends string> = T | (string & Record<never, never>);

export type DirectorySyncSource = Widened<"microsoft_entra" | "scim">;
/** Open like the rest on purpose: nothing in this section renders `trigger`, so
    rejecting a payload over a value we never read would fail the whole surface
    for no gain. The closed set is the server's CHECK to enforce, not ours. */
export type DirectorySyncTrigger = Widened<"manual" | "scheduled">;
export type DirectorySyncStatus = Widened<
  "running" | "succeeded" | "partial" | "failed"
>;

export interface DirectorySyncCounts {
  usersExamined: number;
  usersUpdated: number;
  managersLinked: number;
  groupsUpserted: number;
  groupMembersReconciled: number;
  failureCount: number;
}

export interface DirectorySyncRun {
  runId: string;
  source: DirectorySyncSource;
  trigger: DirectorySyncTrigger;
  triggeredByUserId: string | null;
  status: DirectorySyncStatus;
  startedAt: string;
  finishedAt: string | null;
  counts: DirectorySyncCounts;
  /** Not every entry is a grantable permission — `GroupMembershipTruncated`
      and friends arrive here too, so this is shown as text, never as a
      "grant this" instruction. */
  degradedCapabilities: string[];
  lastErrorCode: string | null;
  lastError: string | null;
}

export interface DirectoryStatusSource {
  source: DirectorySyncSource;
  configured: boolean;
  /** False for the request-driven SCIM writer, which opens no run. Without it
      a null `lastRun` is ambiguous between "never synced" and "this source
      does not work in runs at all". */
  opensRuns: boolean;
  lastActivityAt: string | null;
  lastRun: DirectorySyncRun | null;
}

export interface DirectoryStatus {
  sources: DirectoryStatusSource[];
  /** Live Entra sign-in evidence for this tenant, distinct from a source's
      `configured` flag: that reports what the last DEPLOYMENT observed about
      setup, these are written by real sign-ins as they happen. The members
      roster derives its own per-member sign-in method from the same rows, which
      is why the overview reads these rather than the deployment projection —
      the two contradicted each other on one screen (PB-5404). */
  /** Optional only at the browser boundary for mixed-version rollouts. Current
      Auth always returns it, but an older replica can answer while Shell has
      already rolled forward. In that window the Entra card degrades alone;
      the rest of the directory status remains usable. */
  entra?: { linkedMemberCount: number; lastSignInAt: string | null };
  people: { activeCount: number; suspendedCount: number };
  groups: { activeCount: number };
  /** `available` says whether reporting lines can currently be MAINTAINED;
      `membersWithManagerCount` says how many are stored. The two disagree
      exactly when a permission was withdrawn after a successful run, which is
      why an unavailable hierarchy must never be rendered as a zero count. */
  hierarchy: {
    available: boolean;
    unavailableReason: HierarchyUnavailableReason | null;
    membersWithManagerCount: number;
  };
}

export type HierarchyUnavailableReason = Widened<
  "graph_sync_never_ran" | "manager_read_not_permitted" | "last_sync_failed"
>;

/** Scoped by tenant for the same reason `tenantConfigurationQueryKey` is: an
    admin session can be replaced in place while the route stays mounted, and an
    unscoped key would then serve the previous tenant's directory counts. */
export function directoryStatusQueryKey(
  tenantId: string | null | undefined,
): readonly unknown[] {
  return ["auth", "admin", "directory-status", tenantId ?? "none"] as const;
}

export async function getDirectoryStatus(): Promise<DirectoryStatus> {
  const response = await fetch("/api/auth/admin/directory-status", {
    credentials: "include",
    headers: { Accept: "application/json", ...csrfHeaders() },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      code?: string;
      message?: string;
    } | null;
    throw new Error(
      body?.message ?? body?.code ?? `Request failed (${response.status})`,
    );
  }
  /* Same guard the tenant-config client carries: nothing enforces "200 implies
     JSON" at runtime, and this message is rendered straight to an admin. */
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new Error("The server returned a response we could not read.");
  }
  /* Shape-checked rather than cast, unlike the sibling clients — this one is
     consumed by `sources.map(...)`, so a 200 carrying `{}` (an ingress or a
     proxy answering for the service) would throw inside render and take the
     page down. Failing here instead routes it to the section's error state,
     which is the whole point of giving that section its own query.

     Deliberately a structural check, not a schema validator: it proves only
     what the caller dereferences. Individual field VALUES stay untrusted and
     are still resolved through the fallback lookups in IdentityAdmin, so a
     vocabulary Auth widens ahead of the shell keeps degrading to one verbatim
     row rather than failing the whole read. */
  if (!isDirectoryStatus(payload)) {
    throw new Error("The server returned a response we could not read.");
  }
  return payload;
}

function isDirectoryStatus(value: unknown): value is DirectoryStatus {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate["sources"]) &&
    candidate["sources"].every(isSource) &&
    (candidate["entra"] === undefined || isEntra(candidate["entra"])) &&
    isCounts(candidate["people"], ["activeCount", "suspendedCount"]) &&
    isCounts(candidate["groups"], ["activeCount"]) &&
    isHierarchy(candidate["hierarchy"])
  );
}

/**
 * Every field the contract declares required is checked, not only the ones whose
 * absence would throw.
 *
 * The earlier version checked just enough to survive rendering, on the reasoning
 * that `configured` and `opensRuns` are read as truthiness. That protected the
 * page and not the admin: `configured: "false"` is a non-empty string, so a
 * malformed payload rendered a confident **Connected** for a source that is not
 * connected. A false negative from missing data is a gap; a false positive is
 * this section reporting readiness the tenant does not have, which is the exact
 * failure the whole surface exists to prevent — so a payload that cannot state
 * these facts properly is rejected and shown as an error instead.
 *
 * It also makes the `value is DirectoryStatus` predicate honest. Asserting that
 * type while leaving declared booleans unverified told TypeScript something this
 * function had not established.
 *
 * Forward compatibility is unaffected: this constrains field TYPES, never their
 * values, so a new source key or run status still arrives and still degrades
 * through the fallback lookups in IdentityAdmin.
 */
function isSource(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const source = value as Record<string, unknown>;
  return (
    typeof source["source"] === "string" &&
    typeof source["configured"] === "boolean" &&
    typeof source["opensRuns"] === "boolean" &&
    isNullableString(source["lastActivityAt"]) &&
    // Required by the contract, so an absent key is a contract violation rather
    // than a source that never ran — "Never run" must not be inferred from it.
    "lastRun" in source &&
    (source["lastRun"] === null || isRun(source["lastRun"]))
  );
}

const RUN_COUNT_KEYS = [
  "usersExamined",
  "usersUpdated",
  "managersLinked",
  "groupsUpserted",
  "groupMembersReconciled",
  "failureCount",
] as const;

/**
 * Every field `DirectorySyncRun` declares required, not only the four this section
 * currently reads.
 *
 * Checking just the read fields left `isDirectoryStatus` handing TypeScript a
 * fully-typed run it had verified a third of. The next person to render
 * `lastRun.counts.usersExamined` would compile clean and crash at runtime on a
 * payload this guard accepted — the type claims those fields exist, so the guard
 * should be the reason that is true.
 *
 * A run may still carry fields this build does not know, and `status` is still an
 * unconstrained string so a widened vocabulary keeps arriving.
 */
function isRun(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const run = value as Record<string, unknown>;
  return (
    typeof run["runId"] === "string" &&
    typeof run["source"] === "string" &&
    typeof run["trigger"] === "string" &&
    typeof run["status"] === "string" &&
    typeof run["startedAt"] === "string" &&
    isNullableString(run["triggeredByUserId"]) &&
    isNullableString(run["finishedAt"]) &&
    isCounts(run["counts"], RUN_COUNT_KEYS) &&
    // Joined into one line of admin-facing text, so a non-string entry would
    // read as "[object Object]" rather than fail.
    Array.isArray(run["degradedCapabilities"]) &&
    run["degradedCapabilities"].every((entry) => typeof entry === "string") &&
    isNullableString(run["lastError"]) &&
    isNullableString(run["lastErrorCode"])
  );
}

/** Checked like `isHierarchy`, and for the same reason the source guard states:
    this section renders a COUNT of people and a timestamp straight to an
    administrator, so a payload that cannot state them properly is rejected
    rather than rendered as a confident zero. */
function isEntra(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const entra = value as Record<string, unknown>;
  return (
    isCount(entra["linkedMemberCount"]) &&
    isNullableString(entra["lastSignInAt"])
  );
}

function isHierarchy(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const hierarchy = value as Record<string, unknown>;
  return (
    typeof hierarchy["available"] === "boolean" &&
    isNullableString(hierarchy["unavailableReason"]) &&
    isCount(hierarchy["membersWithManagerCount"])
  );
}

/** Rendered directly as a React child, so a non-string that is not nullish would
    throw ("Objects are not valid as a React child") rather than read oddly. */
function isNullableString(value: unknown): boolean {
  return value === null || typeof value === "string";
}

function isCounts(value: unknown, keys: readonly string[]): boolean {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  return keys.every((key) => isCount(record[key]));
}

/** These are population counts printed verbatim to an administrator, so a
    negative or fractional value is a false fact, not a rendering quirk. */
function isCount(value: unknown): boolean {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}
